import type { WorkitemTag } from '@rhythmic/db/types'
import type { Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import { z } from 'zod'
import { useRhythmicLogger } from '#imports'

import { getWorkitemTagsCollection } from '../../getCollections'

const logger = useRhythmicLogger('useTeamContext:updateWorkitemTag')

export interface UpdateWorkitemTagPayload {
  workitemId: string
  tagId: string
  newTagId: string
  eid: string
  workspaceId?: string | null
}

export interface WorkitemTagMutationContext {
  teamId: string
  workspaceId?: string | null
}

const UpdateWorkitemTagSchema = z.object({
  workitemId: z.string().min(1),
  tagId: z.string().uuid(),
  newTagId: z.string().uuid(),
  eid: z.string().uuid(),
  workspaceId: z.string().uuid().nullable().optional(),
})

function validateParams(payload: UpdateWorkitemTagPayload, context: WorkitemTagMutationContext) {
  if (!context.teamId) throw new Error('Team context is not ready')

  const parsed = UpdateWorkitemTagSchema.parse(payload)

  return {
    ...parsed,
    teamId: context.teamId,
    workspaceId: parsed.workspaceId ?? context.workspaceId ?? null,
  }
}

export default function handleUpdateWorkitemTag(
  payload: UpdateWorkitemTagPayload,
  context: WorkitemTagMutationContext,
): Transaction {
  const resolved = validateParams(payload, context)
  const workitemTagsCollection = getWorkitemTagsCollection(resolved.teamId)

  const action = createOptimisticAction<{
    payload: UpdateWorkitemTagPayload
    context: WorkitemTagMutationContext
  }>({
    onMutate: () => {
      logger.debug('Optimistically updating workitem tag', {
        eid: resolved.eid,
        tagId: resolved.tagId,
        newTagId: resolved.newTagId,
      })
      workitemTagsCollection.update(resolved.eid, (draft: WorkitemTag) => {
        draft.tag_id = resolved.newTagId
        draft.team_id = resolved.teamId
        draft.workspace_id = resolved.workspaceId
      })
    },
    mutationFn: async () => {
      const result = await $fetch<{ txid?: string | number }>(`/api/workitems/${resolved.workitemId}/tags` as string, {
        method: 'PUT',
        body: {
          tag_id: resolved.tagId,
          new_tag_id: resolved.newTagId,
          team_id: resolved.teamId,
          workspace_id: resolved.workspaceId,
        },
      })

      if (result?.txid && workitemTagsCollection.utils?.awaitTxId)
        await workitemTagsCollection.utils.awaitTxId(Number.parseInt(result.txid.toString()))
    },
  })

  return action({ payload, context })
}
